import { useState } from 'react';
import { DfeInventoryEntry } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface TransferItemDialogProps {
  item: DfeInventoryEntry | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (toUsername: string, quantity?: number) => Promise<void>;
}

export function TransferItemDialog({ item, open, onClose, onConfirm }: TransferItemDialogProps) {
  const [toUsername, setToUsername] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setToUsername('');
    setQuantity('1');
    setError(null);
  };

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!toUsername.trim()) {
      setError('Recipient username is required');
      return;
    }

    if (item?.fungibility === 'fungible') {
      const qty = parseInt(quantity, 10);
      if (!Number.isInteger(qty) || qty < 1) {
        setError('Quantity must be a positive integer');
        return;
      }
      if (item.quantity != null && qty > item.quantity) {
        setError(`Quantity cannot exceed ${item.quantity}`);
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    try {
      const qty = item?.fungibility === 'fungible' ? parseInt(quantity, 10) : undefined;
      await onConfirm(toUsername.trim(), qty);
      resetForm();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Transfer {item?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="toUsername">Recipient Username</Label>
            <Input
              id="toUsername"
              value={toUsername}
              onChange={(e) => setToUsername(e.target.value)}
              placeholder="username"
            />
          </div>
          {item?.fungibility === 'fungible' && (
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity (max: {item.quantity})</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                max={item.quantity ?? undefined}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Transferring...' : 'Transfer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
